import React, { JSX, useEffect, useState, useRef } from "react";
import {
  Box,
  Typography,
  Paper,
  Divider,
  Skeleton,
  useTheme,
  Grid,
  Tooltip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../axios/axios";
import { END_POINTS } from "../../shared";
import eventEmitter from "../../components/utils/eventEmitter";
import total_asset from "../../assets/total_asset.svg";
import risk_asset from "../../assets/risk_asset.svg";
import notscan_asset from "../../assets/notscanned_asset.svg";
import decommissioned_asset from "../../assets/decominnision_asset.svg";
import unassigned_asset from "../../assets/unasigned_asset.svg";
import vpn_asset from "../../assets/vpn_asset.svg";
import agent_asset from "../../assets/agent_asset.svg";
import agent_asset_dark from "../../assets/agent_asset_dark.svg";
import DrawerWrapper from "../../components/drawer/Drawer";
import LaunchSharpIcon from "@mui/icons-material/LaunchSharp";
import CloudOutlinedIcon from "@mui/icons-material/CloudOutlined";
import DesktopWindowsOutlinedIcon from "@mui/icons-material/DesktopWindowsOutlined";
import software_asset from "../../assets/software_asset.svg";
import software_asset_dark from "../../assets/software_asset_dark.svg";
import asset_inventory from "../../assets/asset_inventory_icon.svg";
import asset_inventory_dark from "../../assets/asset_inventory_icon_dark.svg";
import TotalAssets from "./TotalAssets";
import RiskAccepted from "./RiskAccepted";
import AgentLayout from "./AgentLayout";
import AssetsNotScanned from "./AssetsNotScanned";
import DecommissionedAssets from "./DecommissionedAssets";
import UnAssignedAssets from "./UnAssignedAssets";
import VPNLayout from "./VPNLayout";
import Software from "../Software/Software";
import Discovery from "../discovery/Discovery";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import { useDashboardContext } from "../../modules/dashboard/DashboardContext";

const AssetItemSkeleton = ({ height = 40 }: { height?: number }) => {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2, height: height }}>
      <Skeleton
        variant="circular"
        width={32}
        height={32}
        sx={{
          opacity: 0.6,
          flexShrink: 0,
        }}
      />
      <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
        <Skeleton
          variant="text"
          width="60%"
          height={28}
          sx={{
            opacity: 0.8,
            animation: "pulse 1.5s infinite",
            "@keyframes pulse": {
              "0%": { opacity: 0.8 },
              "50%": { opacity: 0.4 },
              "100%": { opacity: 0.8 },
            },
          }}
        />
        <Skeleton
          variant="text"
          width="40%"
          height={16}
          sx={{
            opacity: 0.4,
          }}
        />
      </Box>
    </Box>
  );
};

const Assets: React.FC<{ suppressInitialSkeleton?: boolean }> = ({
  suppressInitialSkeleton = false,
}) => {
  const navigate = useNavigate();
  const [vpnAgentCount, setVpnAgentCount] = useState<{
    vpn: number;
    agent: number;
  }>({ vpn: 0, agent: 0 });
  const [assetsNotScanned, setAssetsNotScanned] = useState<number>(0);
  const [assetsDetails, setAssetsDetails] = useState<{
    total: number;
    unassigned: number;
  }>({ total: 0, unassigned: 0 });
  const [assetCounts, setAssetCounts] = useState<{
    onPremise: number;
    cloud: number;
  }>({ onPremise: 0, cloud: 0 });
  const [assetsResponse, setAssetsResponse] = useState<any>(null);
  const [trigger, setTrigger] = useState(0);
  const [loading, setLoading] = useState(true);
  const [initialSkeletonShown, setInitialSkeletonShown] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerLabel, setDrawerLabel] = useState<string | null>(null);
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const { setIsOverlayOpen } = useDashboardContext();

  // Notify parent context when drawer opens/closes to disable drag and drop
  useEffect(() => {
    setIsOverlayOpen(drawerOpen);
  }, [drawerOpen, setIsOverlayOpen]);
  // const [isNewUserTrue, setIsNewUserTrue] = useState(false);
  // const [isOnlyCloudUser, setIsOnlyCloudUser] = useState(false);
  // const [isOnlyPremAssetUser, setIsOnlyPremAssetUser] = useState(false);
  // const [isbothCloudAndPremAssetUser, setIsBothCloudAndPremAssetUser] =
  //   useState(false);
  const isNewUserTrue = useSelector(
    (state: RootState) => state.UserLoginDetails.isNewUserTrue
  );
  const isOnlyCloudUser = useSelector(
    (state: RootState) => state.UserLoginDetails.isOnlyCloudUser
  );
  const isOnlyPremAssetUser = useSelector(
    (state: RootState) => state.UserLoginDetails.isOnlyPremAssetUser
  );
  const isBothCloudAndPremAssetUser = useSelector(
    (state: RootState) => state.UserLoginDetails.isBothCloudAndPremAssetUser
  );

  // Responsive container tracking
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<number>(0);

  console.log(isOnlyPremAssetUser, "isOnlyPremAssetUser");
  console.log(isOnlyCloudUser, "isOnlyCloudUser");
  console.log(isNewUserTrue, "isNewUserTrue");
  console.log(isBothCloudAndPremAssetUser, "isBothCloudAndPremAssetUser");

  // Track container width for responsive behavior
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const updateWidth = () => {
      if (container) {
        const width = container.offsetWidth;
        setContainerWidth(width);
      }
    };

    // Initial measurement
    updateWidth();

    // Use ResizeObserver for container changes
    const resizeObserver = new ResizeObserver(() => {
      updateWidth();
    });

    resizeObserver.observe(container);

    // Also listen to window resize (for grid layout changes)
    window.addEventListener('resize', updateWidth);

    // Debounced resize handler for better performance
    let timeoutId: NodeJS.Timeout;
    const debouncedResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(updateWidth, 100);
    };
    window.addEventListener('resize', debouncedResize);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateWidth);
      window.removeEventListener('resize', debouncedResize);
    };
  }, []);

  useEffect(() => {
    const handleFilterSaved = () => {
      setInitialSkeletonShown(false);
      setLoading(true);
      setTrigger((prev) => prev + 1);
    };

    eventEmitter.on("filterSaved", handleFilterSaved);

    return () => {
      eventEmitter.off("filterSaved", handleFilterSaved);
    };
  }, []);

  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);
      try {
        // Fetch VPN and Agent count
        // NOTE: Previously fetched via GET from END_POINTS.DASHBOARD_VPN_AGENT_COUNT ('securityscan/dashboard-dashboard-vpn-agent-count').
        // Backend removed 'dashboard-dashboard-vpn-agent-count' API and consolidated the logic into 'dashboard-score' (END_POINTS.DASHBOARD_SCORE).
        // It is now called using POST with payload { module: "dashboard-vpn-agent-count" }.
        const vpnAgentResponse = await axiosInstance.post(
          END_POINTS.DASHBOARD_SCORE,
          {
            module: "dashboard-vpn-agent-count",
          }
        );

        const vpnCountRes = await axiosInstance.get(END_POINTS.VPN);

        if (vpnAgentResponse?.status === 200) {
          setVpnAgentCount({
            vpn: vpnCountRes?.data?.data?.length || 0,
            agent: vpnAgentResponse?.data?.data?.agent_count || 0,
          });
        }

        // Fetch assets not scanned
        // NOTE: Previously fetched via GET from END_POINTS.DASHBOARD_ASSETS_NOT_SCANNED ('securityscan/dashboard-assets-not-scanned').
        // Backend removed 'dashboard-assets-not-scanned' API and consolidated the logic into 'dashboard-score' (END_POINTS.DASHBOARD_SCORE).
        // It is now called using POST with payload { module: "dashboard-assets-not-scanned" }.
        const notScannedResponse = await axiosInstance.post(
          END_POINTS.DASHBOARD_SCORE,
          {
            module: "dashboard-assets-not-scanned",
          }
        );
        if (notScannedResponse?.status === 200) {
          setAssetsNotScanned(
            notScannedResponse?.data?.data?.device_count || 0
          );
        }

        // Fetch assets details
        // NOTE: Previously fetched via GET from END_POINTS.DASHBOARD_ASSETS_DETAILS ('securityscan/dashboard-assets-details').
        // Backend removed 'dashboard-assets-details' API and consolidated the logic into 'dashboard-score' (END_POINTS.DASHBOARD_SCORE).
        // It is now called using POST with payload { module: "dashboard-assets-details" }.
        const assetsResponse = await axiosInstance.post(
          END_POINTS.DASHBOARD_SCORE,
          {
            module: "dashboard-assets-details",
          }
        );
        if (assetsResponse?.status === 200) {
          setAssetsResponse(assetsResponse);
          setAssetsDetails({
            total: assetsResponse?.data?.data?.all_asset_count || 0,
            unassigned: assetsResponse?.data?.data?.unassigned_asset_count || 0,
          });
          setAssetCounts({
            onPremise: assetsResponse?.data?.data?.on_premise_asset_count || 0,
            cloud: assetsResponse?.data?.data?.cloud_asset_count || 0,
          });
        }

        // Fetch benchmark score
        // NOTE: Previously fetched via GET from END_POINTS.DASHBOARD_BENCHMARK_SCORE ('securityscan/dashboard-benchmark-score?module=on_premise&bench_name=CIS').
        // Backend removed 'dashboard-benchmark-score' API and consolidated the logic into 'dashboard-assets' (END_POINTS.DASHBOARD_ASSESTS_LOWEST_SCORE).
        // It is now called using POST with payload { module: "dashboard-benchmark-score", scope: "on_premise", bench_name: "CIS" }.
        const benchmarkResponse = await axiosInstance.post(
          END_POINTS.DASHBOARD_ASSESTS_LOWEST_SCORE,
          {
            module: "dashboard-benchmark-score",
            scope: "on_premise",
            bench_name: "CIS",
          }
        );
        if (benchmarkResponse?.status === 200) {
          setBenchmarkScore(benchmarkResponse?.data?.data?.score || 0);
        }
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setTimeout(() => {
          setLoading(false);
          setInitialSkeletonShown(true);
        }, 600);
      }
    };

    fetchDashboardData();
  }, [trigger]);

  const assetsData = [
    {
      icon: (
        <img
          src={total_asset}
          style={{ color: "#6366F1", fontSize: "2rem" }}
        />
      ),
      value: assetsDetails?.total?.toString() || "0",
      title: "Total Assets",
      label: (
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, color: isDarkMode ? "#fff" : "#8480AD" }}
        >
          Total Assets
        </Typography>
      ),
    },
    {
      icon: (
        <img src={risk_asset} style={{ color: "#6366F1", fontSize: "2rem" }} />
      ),
      value: assetsResponse?.data?.data?.accepted_risk_count?.toString() || "0",
      title: "Risk Accepted",
      label: (
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, color: isDarkMode ? "#fff" : "#8480AD" }}
        >
          Risk Accepted
        </Typography>
      ),
    },
    {
      icon: (
        <img
          src={unassigned_asset}
          style={{ color: "#6366F1", fontSize: "2rem" }}
        />
      ),
      value: assetsDetails?.unassigned?.toString() || "0",
      title: "Unassigned Assets",
      label: (
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, color: isDarkMode ? "#fff" : "#8480AD" }}
        >
          Unassigned Assets
        </Typography>
      ),
    },
    //   {
    //   icon: (
    //     <Box display="flex" alignItems="center" gap={1}>
    //       <img
    //         src={notscan_asset}
    //         style={{ color: "#6366F1", fontSize: "2rem" }}
    //       />
    //       <Typography variant="h6" sx={{ fontWeight: 600 }}>
    //         {assetsNotScanned?.toString() || "0"}
    //       </Typography>
    //       <Typography variant="body2" sx={{ fontWeight: 600,fontSize: "0.7rem", color: isDarkMode ? "#fff" : "#8480AD" }}>
    //         In last 30days
    //       </Typography>
    //     </Box>
    //   ),
    //   label: "Assets Not Scanned",
    //   // label: (
    //   //   <Typography
    //   //     variant="body2"
    //   //     sx={{ fontWeight: 600, color: isDarkMode ? "#fff" : "#8480AD" }}
    //   //   >
    //   //     Assets Not Scanned
    //   //   </Typography>
    //   // ),
    // }
    // ,
    {
      icon: (
        <img
          src={notscan_asset}
          style={{ color: "#6366F1", fontSize: "2rem" }}
        />
      ),
      value: assetsNotScanned?.toString() || "0",
      subLabel: "in last 30days",
      title: "Assets Not Scanned",
      label: (
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, color: isDarkMode ? "#fff" : "#8480AD" }}
        >
          Assets Not Scanned
        </Typography>
      ),
    },
    {
      icon: (
        <img
          src={decommissioned_asset}
          style={{ color: "#6366F1", fontSize: "2rem" }}
        />
      ),
      value:
        assetsResponse?.data?.data?.decommissioned_asset_count?.toString() ||
        "0",
      title: "Decommissioned Assets",
      label: (
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, color: isDarkMode ? "#fff" : "#8480AD" }}
        >
          Decommissioned Assets
        </Typography>
      ),
    },
    {
      icon: (
        <img src={vpn_asset} style={{ color: "#6366F1", fontSize: "2rem" }} />
      ),
      value: vpnAgentCount?.vpn?.toString() || "0",
      title: "VPN(s)",
      label: (
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, color: isDarkMode ? "#fff" : "#8480AD" }}
        >
          VPN(s)
        </Typography>
      ),
    },
    {
      icon: (
        <img
          src={isDarkMode ? agent_asset_dark : agent_asset}
          style={{ color: "#6366F1", fontSize: "2rem" }}
        />
      ),
      value: vpnAgentCount?.agent?.toString() || "0",
      title: "Agent(s)",
      label: (
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, color: isDarkMode ? "#fff" : "#8480AD" }}
        >
          Agent(s)
        </Typography>
      ),
    },
    {
      icon: (
        <img
          src={isDarkMode ? software_asset_dark : software_asset}
          alt="Total Apps"
          style={{ color: "#6366F1", fontSize: "2rem" }}
        />
      ),
      value: assetsResponse?.data?.data?.software_total_count?.toString() || "0",
      title: "Total Apps",
      label: (
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, color: isDarkMode ? "#fff" : "#8480AD" }}
        >
          Total Apps
        </Typography>
      ),
    },
    {
      icon: (
        <img
          src={isDarkMode ? asset_inventory_dark : asset_inventory}
          alt="Assets Discovered"
          style={{ color: "#6366F1", fontSize: "2rem" }}
        />
      ),
      value: assetsResponse?.data?.data?.asset_inventory_count?.toString() || "0",
      title: "Assets Discovered",
      // Plug-and-play: only when BE sends asset_aws_inventry_count
      awsInventoryCount:
        assetsResponse?.data?.data != null &&
        Object.prototype.hasOwnProperty.call(
          assetsResponse.data.data,
          "asset_aws_inventry_count",
        )
          ? Number(assetsResponse.data.data.asset_aws_inventry_count) || 0
          : undefined,
      label: (
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, color: isDarkMode ? "#fff" : "#8480AD" }}
        >
          Assets Discovered
        </Typography>
      ),
    },
  ];

  const renderAssetValue = (item: {
    value: string;
    title: string;
    awsInventoryCount?: number;
  }) => {
    const valueSx = {
      fontWeight: 700,
      lineHeight: 1.2,
      fontSize: containerWidth < 600 ? "0.9rem" : "1rem",
      whiteSpace: "nowrap" as const,
    };
    const valueRow = (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
        <Typography variant="h6" sx={valueSx}>
          {item.value}
        </Typography>
        {item.awsInventoryCount !== undefined && (
          <>
            <Typography
              component="span"
              aria-hidden
              sx={{
                ...valueSx,
                color: isDarkMode ? "rgba(255,255,255,0.35)" : "#D0D5DD",
                fontWeight: 500,
                px: 0.15,
              }}
            >
              |
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                flexShrink: 0,
              }}
            >
              <CloudOutlinedIcon
                sx={{
                  fontSize: containerWidth < 600 ? "1rem" : "1.15rem",
                  color: isDarkMode ? "#A5B4FC" : "#6366F1",
                }}
              />
              <Typography variant="h6" sx={valueSx}>
                {item.awsInventoryCount}
              </Typography>
            </Box>
          </>
        )}
      </Box>
    );

    if (item.title !== "Assets Discovered" || item.awsInventoryCount === undefined) {
      return valueRow;
    }

    // Option E: compact hover — On-prem first, then Cloud
    return (
      <Tooltip
        arrow
        placement="bottom-start"
        enterDelay={120}
        title={
          <Box sx={{ py: 0.25, px: 0.15, minWidth: 0 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1.25,
                mb: 0.5,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <DesktopWindowsOutlinedIcon sx={{ fontSize: 12, color: "#0F766E" }} />
                <Typography sx={{ fontSize: "11px", fontWeight: 500, lineHeight: 1.2 }}>
                  On-prem
                </Typography>
              </Box>
              <Typography sx={{ fontSize: "11px", fontWeight: 700, lineHeight: 1.2 }}>
                {item.value}
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1.25,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <CloudOutlinedIcon sx={{ fontSize: 12, color: "#6366F1" }} />
                <Typography sx={{ fontSize: "11px", fontWeight: 500, lineHeight: 1.2 }}>
                  Cloud
                </Typography>
              </Box>
              <Typography sx={{ fontSize: "11px", fontWeight: 700, lineHeight: 1.2 }}>
                {item.awsInventoryCount}
              </Typography>
            </Box>
          </Box>
        }
        slotProps={{
          tooltip: {
            sx: {
              bgcolor: isDarkMode ? "#2A303C" : "#fff",
              color: isDarkMode ? "#F3F4F6" : "#101828",
              border: "1px solid",
              borderColor: isDarkMode ? "#4B5563" : "#D0D5DD",
              boxShadow: "0 6px 14px rgba(16,24,40,0.12)",
              borderRadius: "6px",
              p: "6px 8px",
              maxWidth: "none",
            },
          },
          arrow: {
            sx: { color: isDarkMode ? "#2A303C" : "#fff" },
          },
        }}
      >
        <Box sx={{ display: "inline-flex", cursor: "default" }}>{valueRow}</Box>
      </Tooltip>
    );
  };

  const renderDrawerContent = () => {
    switch (drawerLabel) {
      case "Total Assets":
        return <TotalAssets />;
      case "Risk Accepted":
        return <RiskAccepted />;
      case "Unassigned Assets":
        return <UnAssignedAssets />;
      case "Assets Not Scanned":
        return <AssetsNotScanned />;
      case "Decommissioned Assets":
        return <DecommissionedAssets />;
      case "VPN(s)":
        return <VPNLayout />;
      case "Agent(s)":
        return <AgentLayout />;
      case "Total Apps":
        return (
          <Box sx={{ p: 0, width: '100%', height: '100%' }}>
            <Software fitDrawer />
          </Box>
        );
      case "Assets Discovered":
        return (
          <Box sx={{ p: 0, width: '100%', height: '100%' }}>
            <Discovery fitDrawer />
          </Box>
        );
      default:
        return <div>No details available</div>;
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        padding: isOnlyCloudUser ? 0.75 : 1,
        borderRadius: "4px",
        bgcolor: theme.palette.mode === "dark" ? "background.paper" : "white",
        // boxShadow:
        //   theme.palette.mode === "dark"
        //     ? "0px 2px 6px rgba(0, 0, 0, 0.25)"
        //     : "0px 2px 6px rgba(0, 0, 0, 0.08)",
        border: "1px solid",
        borderColor: "divider",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        //boxShadow: isDarkMode ?  '1px 1px 1px 1px rgba(25, 25, 25, 0.5)' :'1px 1px 1px 1px rgba(235, 235, 235, 0.5)',
      }}
    >
      <div>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 550,
            fontSize: "14px",
            height: "35px",
            mt: 1,
            ml: 1,
          }}
        >
          {isOnlyCloudUser ? "Summary" : "Assets Information"}
        </Typography>
      </div>
      <div>
        <Divider sx={{ mt: -1, }} />
      </div>
      <Box
        ref={containerRef}
        sx={{
          display: "flex",
          alignItems: "flex-start",
          overflowX: "hidden",
          overflowY: "auto",
          flexGrow: 1,
          minHeight: 0,
          pt: isOnlyCloudUser ? 1 : 0.75,
          pb: isOnlyCloudUser ? 1 : 0.5,
          pr: 0.5,
          width: "100%",
          justifyContent: "flex-start",
          "&::-webkit-scrollbar": {
            width: "4px",
          },
          "&::-webkit-scrollbar-track": {
            background: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.05)",
            borderRadius: "10px",
          },
          "&::-webkit-scrollbar-thumb": {
            background: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.2)",
            borderRadius: "10px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.35)" : "rgba(0, 0, 0, 0.3)",
          },
        }}
      >
        {loading && !initialSkeletonShown && !suppressInitialSkeleton ? (
          isOnlyPremAssetUser || isOnlyCloudUser ? (
            <Grid container direction="column" spacing={2}>
              {[0, 1, 2].map((rowIdx) => (
                <Grid item key={`skeleton-row-${rowIdx}`}>
                  <Grid
                    container
                    spacing={0}
                    alignItems={isOnlyCloudUser ? "center" : "flex-start"}
                  >
                    {[0, 1, 2].map((colIdx) => (
                      <React.Fragment key={`skeleton-item-${rowIdx}-${colIdx}`}>
                        {colIdx !== 0 && (
                          <Grid item>
                            <Divider
                              orientation="vertical"
                              flexItem
                              sx={{
                                height: "40px",
                                alignSelf: "center",
                                color:
                                  theme.palette.mode === "dark"
                                    ? "#fff"
                                    : "#CCCAE7",
                              }}
                            />
                          </Grid>
                        )}
                        <Grid item xs>
                          <Box sx={{ minWidth: 120, px: 2 }}>
                            <AssetItemSkeleton height={isOnlyCloudUser || isOnlyPremAssetUser ? 53 : 40} />
                          </Box>
                        </Grid>
                      </React.Fragment>
                    ))}
                  </Grid>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {Array(9)
                .fill(null)
                .map((_, index) => (
                  <React.Fragment key={index}>
                    <Box sx={{ minWidth: 120, px: 2 }}>
                      <AssetItemSkeleton height={isOnlyCloudUser || isOnlyPremAssetUser ? 60 : 40} />
                    </Box>
                    {index < 8 && (
                      <Divider
                        orientation="vertical"
                        flexItem
                        sx={{ height: "40px", alignSelf: "center", mx: 0 }}
                      />
                    )}
                  </React.Fragment>
                ))}
            </Box>
          )
        ) : isBothCloudAndPremAssetUser ? (
          <Box
            sx={{
              display: containerWidth >= 1200 ? 'flex' : 'grid',
              justifyContent: containerWidth >= 1200 ? 'space-between' : 'initial',
              gridTemplateColumns: containerWidth >= 1200 ? 'none' : `repeat(${containerWidth >= 900 ? 5 : containerWidth >= 600 ? 4 : containerWidth >= 400 ? 2 : 1}, 1fr)`,
              gap: 2,
              width: '100%',
              alignItems: 'flex-start'
            }}
          >
            {assetsData.map((item, index) => (
              <Box
                key={index}
                className={`no-drag ${Number(item.value) > 0 ? 'clickable-area' : 'no-click'}`}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  justifyContent: "flex-start",
                  gap: 1,
                  width: containerWidth >= 1200 ? "auto" : "100%",
                  cursor: Number(item.value) > 0 ? "pointer" : "default",
                }}
                onClick={() => {
                  if (
                    [
                      "Risk Accepted",
                      "Unassigned Assets",
                      "VPN(s)",
                      "Agent(s)",
                      "Assets Not Scanned",
                      "Decommissioned Assets",
                      "Total Apps",
                      "Assets Discovered",
                    ].includes(item.title) &&
                    Number(item.value) > 0
                  ) {
                    setDrawerLabel(item.title);
                    setDrawerOpen(true);
                  } else if (
                    item.title === "Total Assets" &&
                    (assetCounts.onPremise > 0 || assetCounts.cloud > 0)
                  ) {
                    setDrawerLabel(item.title);
                    setDrawerOpen(true);
                  }
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: '100%', minWidth: 0 }}>
                  <Box sx={{ flexShrink: 0 }}>
                    {item.icon}
                  </Box>
                  {/* Value and subLabel in same row for 'Assets Not Scanned' */}
                  {item.title === "Assets Not Scanned" ? (
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5, flex: 1, minWidth: 0 }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          lineHeight: 1.2,
                          fontSize: containerWidth < 600 ? "0.9rem" : "1rem",
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {item.value}
                      </Typography>
                      {item.subLabel && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            fontSize: containerWidth < 600 ? "0.65rem" : "0.7rem",
                            ml: 0.5,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {item.subLabel}
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    renderAssetValue(item)
                  )}
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    width: '100%',
                    minWidth: 0,
                  }}
                >
                  {/* Only show subLabel below for items other than 'Assets Not Scanned' */}
                  {item.subLabel && item.title !== "Assets Not Scanned" && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: "block",
                        lineHeight: 1.2,
                        fontSize: containerWidth < 600 ? "0.6rem" : "0.65rem",
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        width: '100%',
                      }}
                    >
                      {item.subLabel}
                    </Typography>
                  )}
                  {typeof item.label === "string" ? (
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        lineHeight: 1.2,
                        fontSize: item.subLabel ? (containerWidth < 600 ? "0.6rem" : "0.65rem") : (containerWidth < 600 ? "0.65rem" : "0.7rem"),
                        color:
                          theme.palette.mode === "dark" ? "#fff" : "#000000",
                        textAlign: "left",
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        width: '100%',
                      }}
                    >
                      {item.label || item.title}
                    </Typography>
                  ) : (
                    item.label
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        ) : (
          <Grid container direction="column" spacing={0}>
            {(() => {
              // Ensure we have complete rows of 3 items
              const completeRows = Math.ceil(assetsData.length / 3);
              const rows: JSX.Element[] = [];

              for (let rowIndex = 0; rowIndex < completeRows; rowIndex++) {
                const startIndex = rowIndex * 3;
                const rowItems = assetsData.slice(startIndex, startIndex + 3);

                // Fill the last row with 3 items if needed
                while (rowItems.length < 3) {
                  rowItems.push({
                    icon: <Box sx={{ width: 32, height: 32 }} />, // Empty placeholder
                    value: "",
                    title: "",
                    label: <Box sx={{ width: 80, height: 20 }} />, // Empty placeholder
                  });
                }

                rows.push(
                  <Grid item key={`row-${rowIndex}`}>
                    <Grid
                      container
                      spacing={0}
                      alignItems={isOnlyCloudUser ? "center" : "flex-start"}
                      sx={{ mt: 0, mb: 0.5 }}
                    >
                      {rowItems.map((item, idx) => (
                        <React.Fragment key={`item-${startIndex + idx}`}>
                          {/* Divider before each item except the first, and only if it's a real item */}
                          {idx !== 0 && item.title && (
                            <Grid item>
                              <Divider
                                orientation="vertical"
                                flexItem
                                sx={{
                                  height: "40px",
                                  alignSelf: "center",
                                  color:
                                    theme.palette.mode === "dark"
                                      ? "#fff"
                                      : "#CCCAE7",
                                }}
                              />
                            </Grid>
                          )}

                          <Grid
                            item
                            xs
                            sx={{
                              display: "flex",
                              justifyContent: "flex-start",
                            }}
                          >
                            {item.title ? ( // Only render if it's a real item
                              <Box
                                className={`no-drag ${Number(item.value) > 0 ? 'clickable-area' : 'no-click'}`}
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "flex-start", // Left align items
                                  gap: isOnlyPremAssetUser
                                    ? 0
                                    : isOnlyCloudUser
                                      ? 0.5
                                      : 0.75,
                                  px: 2,
                                  py: isOnlyPremAssetUser
                                    ? -10
                                    : isOnlyCloudUser
                                      ? 0
                                      : 0,
                                  mt: 0,
                                  mb: 0,
                                  borderRadius: 2,
                                  cursor: Number(item.value) > 0 ? "pointer" : "default",
                                }}
                                onClick={() => {
                                  if (
                                    [
                                      "Risk Accepted",
                                      "Unassigned Assets",
                                      "VPN(s)",
                                      "Agent(s)",
                                      "Assets Not Scanned",
                                      "Decommissioned Assets",
                                      "Total Apps",
                                      "Assets Discovered",
                                    ].includes(item.title) &&
                                    Number(item.value) > 0
                                  ) {
                                    setDrawerLabel(item.title);
                                    setDrawerOpen(true);
                                  } else if (
                                    item.title === "Total Assets" &&
                                    (assetCounts.onPremise > 0 || assetCounts.cloud > 0)
                                  ) {
                                    setDrawerLabel(item.title);
                                    setDrawerOpen(true);
                                  }
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    justifyContent: "flex-start",
                                    width: "100%",
                                  }}
                                >
                                  {item.icon}
                                  {renderAssetValue(item)}
                                </Box>
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "flex-start",
                                  }}
                                >
                                  {item.subLabel && (
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontSize: "0.65rem",
                                        lineHeight: 1.2,
                                        textAlign: "left",
                                      }}
                                    >
                                      {item.subLabel}
                                    </Typography>
                                  )}
                                  {typeof item.label === "string" ? (
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontSize: "0.7rem",
                                        lineHeight: 1.2,
                                        textAlign: "left",
                                      }}
                                    >
                                      {item.label || item.title}
                                    </Typography>
                                  ) : (
                                    item.label
                                  )}
                                </Box>
                              </Box>
                            ) : (
                              // Render empty placeholder to maintain grid structure
                              <Box sx={{ minWidth: 120, px: 2 }} />
                            )}
                          </Grid>
                        </React.Fragment>
                      ))}
                    </Grid>
                  </Grid>
                );
              }

              return rows;
            })()}
          </Grid>
        )}
      </Box>
      <DrawerWrapper
        styles={{ width: "50%", padding: 0, margin: 0 }}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        header={
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: 3,
            }}
          >
            <Typography fontSize="16px" fontWeight={700}>
              {drawerLabel}
            </Typography>
            {drawerLabel !== "Total Assets" && (
              <LaunchSharpIcon
                sx={{
                  color: isDarkMode ? "white" : "black",
                  cursor: "pointer",
                  fontSize: "20px",
                }}
                onClick={() => {
                  switch (drawerLabel) {
                    case "Unassigned Assets":
                    case "Decommissioned Assets":
                      navigate("/on-premises/assets");
                      break;
                    case "Risk Accepted":
                      navigate("/on-premises/acceptedrisk");
                      break;
                    case "VPN(s)":
                      navigate("/organization/vpn");
                      break;
                    case "Agent(s)":
                      navigate("/organization/agent");
                      break;
                    case "Assets Not Scanned":
                      navigate("/on-premises/assets");
                      break;
                    case "Total Apps":
                      navigate("/on-premises/installed-software");
                      break;
                    case "Assets Discovered":
                      navigate("/discovery");
                      break;
                    default:
                      navigate("/dashboard");
                  }
                }}
              />
            )}
          </div>
        }
        body={renderDrawerContent()}
      // styles={{ width: "50%", padding: 0, margin: 0 }}
      />
    </Paper>
  );
};

export default Assets;
